import { useEffect, useMemo, useRef, useState } from "react";
import { useTransition } from "@react-spring/web";
import {
  HISTORY_OPERATION_TYPE,
  RECORD_STATE,
  UserHistoryParams,
  UserHistoryResponse,
} from "@letsbit/malcolmlb";
import { useMemberHistory } from "hooks/user/useMemberHistory";
import { HistoryFilter } from "./types";
import { useIntroducingBrokerMemberHistory } from "hooks/partnerApi/useIntroducingBrokerMemberHistory";

export const historyFiltersToParams = (
  filter: HistoryFilter,
): UserHistoryParams => {
  const from = filter?.from ? new Date(filter.from) : undefined;
  from?.setUTCHours(0, 0, 0, 0);

  const to = filter?.to ? new Date(filter.to) : undefined;
  to?.setUTCHours(23, 59, 59, 999);

  const cardFilter = filter?.operationType?.includes(
    HISTORY_OPERATION_TYPE.CARD_TRANSACTIONS,
  );

  return (
    filter && {
      currency: cardFilter
        ? undefined
        : filter?.currencies
          ? Array.from(filter?.currencies)
          : undefined,
      operation: filter?.operationType,
      card_ids: filter?.cards ? Array.from(filter?.cards) : undefined,
      states: filter?.states
        ? (Array.from(filter?.states) as RECORD_STATE[])
        : undefined,
      from: from,
      to: to,
      limit: filter.limit,
    }
  );
};

export function useHistoryInfiniteScroll<T extends HTMLElement>(
  historyCardHeight: number,
  params?: UserHistoryParams,
  firstPageOnly?: boolean,
  member_uid?: string,
) {
  const listContainerRef = useRef<T | null>(null);
  const [limit, setLimit] = useState<number | null>(null);
  const refMap = useMemo(() => new WeakMap(), []);
  // const infScrollRef = React.useRef<HTMLIonInfiniteScrollElement | null>(null);
  const config = {
    tension: 125,
    friction: 20,
    precision: 0.0001,
    // duration: 350,
  };

  useEffect(() => {
    if (
      listContainerRef.current &&
      listContainerRef.current.clientHeight &&
      listContainerRef.current.clientHeight > 0 &&
      !limit
    ) {
      const ratio = listContainerRef.current.clientHeight / historyCardHeight;

      setLimit(firstPageOnly ? Math.floor(ratio) - 2 : Math.floor(ratio + 2));
    }
  }, [listContainerRef, limit, historyCardHeight, firstPageOnly]);

  const { limit: limitParam, ...filterParams } = params || {};
  const { data: dataExp, ...swr } = member_uid
    ? useIntroducingBrokerMemberHistory(
        limit
          ? {
              limit,
              member_uid,
              ...filterParams,
            }
          : {
              limit: limitParam,
              member_uid,
              ...filterParams,
            },
      )
    : useMemberHistory(
        limit
          ? {
              limit,
              ...filterParams,
            }
          : {
              limit: limitParam,
              ...filterParams,
            },
      );

  const data = dataExp?.flat() || [];

  const itemsTransitions = useTransition(data, {
    from: { opacity: 0, x: 50, height: historyCardHeight + 8 },
    keys: (item) => item.date + item.amount,
    enter: (item) => async (next) => {
      await next({
        opacity: 1,
        // This + 16 is for the margin top of the element
        height: refMap.get(item)?.offsetHeight + 8,
        x: 0,
      });
    },
    leave: [{ x: 50, opacity: 0 }, { height: 0 }],
    config,
  });

  const forwardedRef = (item: UserHistoryResponse) => (ref: HTMLDivElement) => {
    ref && refMap.set(item, ref);
  };

  return {
    listContainerRef,
    itemsTransitions,
    forwardedRef,
    hasMore: !dataExp || dataExp[dataExp.length - 1].length > 0,
    data,
    ...swr,
  };
}
